import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, CalendarCheck, Mail, TrendingUp, FileSpreadsheet } from "lucide-react";
import { buildDownloadCsv, downloadCsvFile } from "@/lib/downloadCsv";

interface Attribution {
  total: number;
  attributed: number;
  visitors: number;
  medianHours: number | null;
}

interface Stats {
  days: number;
  totals: {
    downloads: number;
    downloadVisitors: number;
    convertedVisitors: number;
    conversionRate: number;
  };
  bookings: Attribution;
  enquiries: Attribution;
  files: {
    file_slug: string;
    downloads: number;
    visitors: number;
    converted: number;
    conversionRate: number;
  }[];
  languages: {
    language: string;
    downloads: number;
    visitors: number;
    share: number;
  }[];
  daily: {
    date: string;
    downloads: number;
    visitors: number;
    files: Record<string, number>;
  }[];
}

const RANGES = [7, 30, 90, 365];

const FILE_LABELS: Record<string, string> = {
  "broschuere-2025": "EAP Broschüre 2025",
  "leitfaden-technikerzettel": "Leitfaden Technikerzettel",
  "studien-zusammenfassung-2026": "Studien-Zusammenfassung 2026",
  "ai-press-onepager": "AI & Press One-Pager",
};

const LANG_LABELS: Record<string, string> = {
  de: "Deutsch",
  en: "Englisch",
  fr: "Französisch",
  nl: "Niederländisch",
  it: "Italienisch",
  es: "Spanisch",
  unbekannt: "Unbekannt",
};

const AdminDownloads = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke<Stats>(
          "download-conversions",
          { body: { days } },
        );
        if (error) throw error;
        if (active) setStats(data as Stats);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [days]);

  const exportCsv = () => {
    if (!stats) return;
    const csv = buildDownloadCsv(stats, { files: FILE_LABELS, languages: LANG_LABELS });
    const today = new Date().toISOString().slice(0, 10);
    downloadCsvFile(csv, `eap-downloads-conversions-${stats.days}tage-${today}.csv`);
  };

  const fmtHours = (h: number | null) =>
    h === null ? "–" : h < 24 ? `${h.toFixed(1)} h` : `${(h / 24).toFixed(1)} Tage`;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
              <Link to="/admin">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">Downloads &amp; Conversions</h1>
            <p className="text-sm text-muted-foreground">
              Wie oft führen Downloads zu Kontaktanfragen oder Videocall-Buchungen?
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r}
                size="sm"
                variant={days === r ? "default" : "outline"}
                onClick={() => setDays(r)}
              >
                {r === 365 ? "12 Mon." : `${r} Tage`}
              </Button>
            ))}
            <Button size="sm" variant="secondary" disabled={!stats || loading} onClick={exportCsv}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV-Export
            </Button>
          </div>
        </div>

        {loading && <p className="text-muted-foreground">Lade Auswertung …</p>}
        {error && <p className="text-destructive text-sm">Fehler: {error}</p>}

        {stats && !loading && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <Download className="w-4 h-4" /> Downloads
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats.totals.downloads}</p>
                  <p className="text-xs text-muted-foreground">
                    {stats.totals.downloadVisitors} unterschiedliche Besucher
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Conversion-Rate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {stats.totals.conversionRate.toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats.totals.convertedVisitors} von {stats.totals.downloadVisitors} Downloadern
                    haben danach Kontakt aufgenommen
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <CalendarCheck className="w-4 h-4" /> Videocall-Buchungen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {stats.bookings.attributed}
                    <span className="text-base font-normal text-muted-foreground">
                      {" "}
                      / {stats.bookings.total}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    nach Download · Ø Zeitspanne {fmtHours(stats.bookings.medianHours)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Kontaktanfragen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {stats.enquiries.attributed}
                    <span className="text-base font-normal text-muted-foreground">
                      {" "}
                      / {stats.enquiries.total}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    nach Download · Ø Zeitspanne {fmtHours(stats.enquiries.medianHours)}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle className="text-lg">Pro Datei</CardTitle>
                <CardDescription>
                  Downloads, Besucher und daraus entstandene Anfragen/Buchungen
                </CardDescription>
              </CardHeader>
              <CardContent className="min-w-0">
                {stats.files.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Noch keine Downloads im gewählten Zeitraum.
                  </p>
                ) : (
                  <div className="w-0 min-w-full overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-2 pr-4">Datei</th>
                          <th className="py-2 pr-4">Downloads</th>
                          <th className="py-2 pr-4">Besucher</th>
                          <th className="py-2 pr-4">Conversions</th>
                          <th className="py-2">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.files.map((f) => (
                          <tr key={f.file_slug} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">
                              {FILE_LABELS[f.file_slug] ?? f.file_slug}
                            </td>
                            <td className="py-2 pr-4">{f.downloads}</td>
                            <td className="py-2 pr-4">{f.visitors}</td>
                            <td className="py-2 pr-4">{f.converted}</td>
                            <td className="py-2">
                              <Badge variant={f.conversionRate > 0 ? "default" : "secondary"}>
                                {f.conversionRate.toFixed(1)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="min-w-0 overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg">Pro Sprache</CardTitle>
                  <CardDescription>
                    In welcher Sprachversion die Dateien geladen werden
                  </CardDescription>
                </CardHeader>
                <CardContent className="min-w-0">
                  {stats.languages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Noch keine Downloads im gewählten Zeitraum.
                    </p>
                  ) : (
                    <div className="w-0 min-w-full overflow-x-auto"><table className="w-full min-w-[440px] text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-2 pr-4">Sprache</th>
                          <th className="py-2 pr-4">Downloads</th>
                          <th className="py-2 pr-4">Besucher</th>
                          <th className="py-2">Anteil</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.languages.map((l) => (
                          <tr key={l.language} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">
                              {LANG_LABELS[l.language] ?? l.language.toUpperCase()}
                            </td>
                            <td className="py-2 pr-4">{l.downloads}</td>
                            <td className="py-2 pr-4">{l.visitors}</td>
                            <td className="py-2">
                              <Badge variant="secondary">{l.share.toFixed(0)}%</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  )}
                </CardContent>
              </Card>

              <Card className="min-w-0 overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg">Pro Tag</CardTitle>
                  <CardDescription>
                    Downloads nach Datum (neueste zuerst), inkl. Aufteilung pro Datei
                  </CardDescription>
                </CardHeader>
                <CardContent className="min-w-0">
                  {stats.daily.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Noch keine Downloads im gewählten Zeitraum.
                    </p>
                  ) : (
                    <div className="max-h-80 w-0 min-w-full overflow-auto">
                      <table className="w-full min-w-[480px] text-sm">
                        <thead className="sticky top-0 bg-card">
                          <tr className="text-left text-muted-foreground border-b">
                            <th className="py-2 pr-4">Datum</th>
                            <th className="py-2 pr-4">Downloads</th>
                            <th className="py-2">Dateien</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.daily.map((d) => (
                            <tr key={d.date} className="border-b last:border-0 align-top">
                              <td className="py-2 pr-4 font-medium whitespace-nowrap">
                                {new Date(d.date).toLocaleDateString("de-DE")}
                              </td>
                              <td className="py-2 pr-4">{d.downloads}</td>
                              <td className="py-2 text-muted-foreground">
                                {Object.entries(d.files)
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([slug, n]) => `${FILE_LABELS[slug] ?? slug} (${n})`)
                                  .join(", ")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <p className="text-xs text-muted-foreground">
              Zuordnung über eine pseudonyme, geräteinterne Besucher-Kennung (localStorage) – keine
              IP-Adressen, keine personenbezogene Profilbildung. Anfragen ohne vorherigen Download
              erscheinen nur in der Gesamtzahl.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDownloads;
